"use client"

import { usePathname } from "next/navigation"

import { Popover, PopoverPanel, Transition } from "@headlessui/react"
import { XMark } from "@medusajs/icons"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import {
  isPostPurchasePath,
  toChromeActions,
} from "@modules/layout/lib/chrome-actions-view-model"
import { getTraderMenuLinks } from "@modules/trader-menu/lib/trader-menu-view-model"
import { Fragment } from "react"

// Menu mobile do chrome Virtual Europe (Story 2.7). O Nav só o mostra <768px (md:hidden).
// Espelha a nav com destinos REAIS + CTA (a landing é story futura). Overlay de vidro dark;
// Esc e clique no backdrop/link fecham (mecânica Popover do Headless UI preservada).
// Single-region (Story 1.1 / AC2): seletores de região/idioma continuam removidos.
//
// QA #15 — só os DESTINOS são fixos; as ações de sessão vêm do `chrome-actions-view-model`,
// a mesma fonte que o Nav lê. Antes eram duas listas à mão e o mobile derivava sozinho.
const SideMenuDestinations = {
  Início: "/",
  Planos: "/#planos",
}

type SideMenuProps = {
  isAuthenticated: boolean
}

const SideMenu = ({ isAuthenticated }: SideMenuProps): React.JSX.Element => {
  const pathname = usePathname()
  // Story 10.7 — os 5 destinos do Menu do Trader, da MESMA fonte que a nav desktop e o rodapé.
  const traderMenuLinks = getTraderMenuLinks()
  // Story 3.8 — não vender a quem acabou de comprar (ecrã de confirmação).
  const actions = toChromeActions(isAuthenticated, {
    isPostPurchase: isPostPurchasePath(pathname),
  })
  const linkActions = actions.filter((action) => action.variant === "link")
  // Autenticado não tem ação `primary` — o bloco do CTA some e sobra só o rodapé.
  const primaryAction = actions.find((action) => action.variant === "primary")

  return (
    <div className="h-full">
      <div className="flex items-center h-full">
        <Popover className="h-full flex">
          {({ open, close }) => (
            <>
              <div className="relative flex h-full">
                <Popover.Button
                  data-testid="nav-menu-button"
                  aria-label="Abrir menu"
                  // Issue #26 — `min-h-11 min-w-11` (44px, WCAG 2.5.5). Este é o ÚNICO acesso a
                  // Início/Planos/Entrar abaixo de 768px e media 24×24px. O `min-*` não depende
                  // da cadeia `h-full`, que já colapsou uma vez.
                  className="relative flex h-full min-h-11 min-w-11 items-center justify-center transition-all ease-out duration-200 focus:outline-none text-text hover:text-accent"
                >
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <path
                      d="M3 6h18M3 12h18M3 18h18"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                </Popover.Button>
              </div>

              {open && (
                <div
                  className="fixed inset-0 z-[50] bg-black/40 pointer-events-auto"
                  onClick={close}
                  data-testid="side-menu-backdrop"
                />
              )}

              <Transition
                show={open}
                as={Fragment}
                enter="transition ease-out duration-150"
                enterFrom="opacity-0"
                enterTo="opacity-100 backdrop-blur-2xl"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 backdrop-blur-2xl"
                leaveTo="opacity-0"
              >
                <PopoverPanel className="flex flex-col absolute w-full pr-4 sm:pr-0 sm:w-1/3 2xl:w-1/4 sm:min-w-min h-[calc(100vh-1rem)] z-[51] inset-x-0 text-sm text-text m-2 backdrop-blur-2xl">
                  <div
                    data-testid="nav-menu-popup"
                    className="flex flex-col h-full bg-surface/90 border border-border rounded-large justify-between p-6 backdrop-blur-2xl"
                  >
                    <div className="flex justify-end" id="xmark">
                      <button
                        data-testid="close-menu-button"
                        onClick={close}
                        aria-label="Fechar menu"
                        className="text-text hover:text-accent"
                      >
                        <XMark />
                      </button>
                    </div>
                    <ul className="flex flex-col gap-6 items-start justify-start">
                      {Object.entries(SideMenuDestinations).map(
                        ([name, href]) => {
                          return (
                            <li key={name}>
                              <LocalizedClientLink
                                href={href}
                                className="text-2xl leading-10 text-text hover:text-accent transition-colors"
                                onClick={close}
                                data-testid={`${name.toLowerCase()}-link`}
                              >
                                {name}
                              </LocalizedClientLink>
                            </li>
                          )
                        },
                      )}
                      {linkActions.map((action) => (
                        <li key={action.key}>
                          <LocalizedClientLink
                            href={action.href}
                            className="text-2xl leading-10 text-text hover:text-accent transition-colors"
                            onClick={close}
                            data-testid={`${action.key}-link`}
                          >
                            {action.label}
                          </LocalizedClientLink>
                        </li>
                      ))}
                      {/* Story 10.7 — grupo "Menu do Trader" (as 5 páginas-wrapper). Alvos ≥44px
                          (min-h-11), fecham o menu ao navegar; lista vem do view-model, não à mão. */}
                      <li className="w-full">
                        <span
                          id="side-menu-trader-heading"
                          className="text-xs font-bold uppercase tracking-wide text-accent"
                        >
                          Menu do Trader
                        </span>
                        <ul
                          className="mt-2 flex flex-col"
                          aria-labelledby="side-menu-trader-heading"
                        >
                          {traderMenuLinks.map((link) => (
                            <li key={link.key}>
                              <LocalizedClientLink
                                href={link.href}
                                className="flex min-h-11 items-center text-lg text-text hover:text-accent transition-colors"
                                onClick={close}
                                data-testid={`side-menu-trader-${link.key}`}
                              >
                                {link.label}
                              </LocalizedClientLink>
                            </li>
                          ))}
                        </ul>
                      </li>
                    </ul>
                    <div className="flex flex-col gap-y-4">
                      {primaryAction && (
                        <LocalizedClientLink
                          href={primaryAction.href}
                          onClick={close}
                          className="rounded-[8px] bg-blue-600 px-4 py-3 text-center text-sm font-semibold text-white transition-colors hover:bg-blue-500"
                        >
                          {primaryAction.label}
                        </LocalizedClientLink>
                      )}
                      <p className="txt-compact-small text-muted">
                        © {new Date().getFullYear()} Virtual Europe. Todos os
                        direitos reservados.
                      </p>
                    </div>
                  </div>
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>
      </div>
    </div>
  )
}

export default SideMenu
